import React, { useEffect, useMemo, useState } from 'react';
import {
  AdminUserDto,
  AdminWatchlistItemDto,
  AdminWatchlistUpsertRequest,
  adminApi,
  isUnauthorized,
} from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminWatchlistsScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

const AdminWatchlistsScreen: React.FC<AdminWatchlistsScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      inputBg: theme.inputBg,
      primary: theme.primary,
      danger: theme.danger,
      radius: theme.radius,
    }),
    [theme]
  );

  const [items, setItems] = useState<AdminWatchlistItemDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    username: '',
    category: '',
    symbol: '',
  });

  const [isUpsertOpen, setIsUpsertOpen] = useState(false);
  const [upserting, setUpserting] = useState(false);
  const [upsertError, setUpsertError] = useState<string | null>(null);
  const [draft, setDraft] = useState<AdminWatchlistUpsertRequest>({
    userId: 0,
    symbol: '',
    category: '',
    buyPriceAlert: null,
    sellPriceAlert: null,
    status: 'Not Reached',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    };

    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    return base;
  };

  const normalizeSymbol = (v: unknown): string => (v ?? '').toString().trim().toUpperCase();

  const refresh = async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminApi.listWatchlists(token, {
        username: filters.username.trim() || undefined,
        category: filters.category.trim() || undefined,
      });
      setItems(list);
      if (opts?.clearSelection) setSelectedIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading watchlists');
      setItems(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh({ clearSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const list = await adminApi.listUsers(token);
        if (cancelled) return;
        setUsers(list);
      } catch (e) {
        if (isUnauthorized(e)) {
          onUnauthorized();
          return;
        }
        if (cancelled) return;
        setUsers(null);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, onUnauthorized]);

  const filteredItems = useMemo(() => {
    const list = items ?? [];
    const symQ = normalizeSymbol(filters.symbol);
    if (!symQ) return list;
    return list.filter((x) => normalizeSymbol(x.symbol).includes(symQ));
  }, [filters.symbol, items]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.username ?? '').toString().trim())
      .filter((u) => u.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const categoryOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.userGroup ?? '').toString().trim())
      .filter((c) => c.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const columns = useMemo(
    () =>
      [
        { key: 'select', title: '', width: 48 },
        { key: 'id', title: 'ID', width: 90 },
        { key: 'userId', title: 'User ID', width: 110 },
        { key: 'username', title: 'Username', width: 160 },
        { key: 'symbol', title: 'Symbol', width: 110 },
        { key: 'currentPrice', title: 'Current Price', width: 140 },
        { key: 'changePct', title: 'Change %', width: 120 },
        { key: 'buyPriceAlert', title: 'Buy Alert', width: 120 },
        { key: 'sellPriceAlert', title: 'Sell Alert', width: 120 },
        { key: 'status', title: 'Status', width: 160 },
        { key: 'updatedAtUtc', title: 'Updated', width: 200 },
      ] as const,
    []
  );

  const cellStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: 13,
    color: colors.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    position: 'sticky',
    top: 0,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    zIndex: 1,
    fontSize: 12,
    fontWeight: 900,
    color: colors.subText,
    textAlign: 'left',
  };

  const parseNumberOrNull = (raw: string): number | null => {
    const t = raw.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const ok = window.confirm(`Delete ${ids.length} selected watchlist item(s)?`);
    if (!ok) return;

    setLoading(true);
    setError(null);
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await adminApi.deleteWatchlistItem(token, id);
      }
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting items');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: colors.radius,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: colors.text, marginBottom: 2 }}>Watchlists</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.subText }}>View & manage watchlist rows per user</div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" style={buttonStyle('neutral')} onClick={() => void refresh({ clearSelection: true })} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            type="button"
            style={buttonStyle('danger', loading || selectedIds.size === 0)}
            disabled={loading || selectedIds.size === 0}
            onClick={() => void deleteSelected()}
          >
            Delete ({selectedIds.size})
          </button>
          <button
            type="button"
            style={buttonStyle('primary')}
            onClick={() => {
              setUpsertError(null);
              setDraft({ userId: 0, symbol: '', buyPriceAlert: null, sellPriceAlert: null, status: 'Not Reached', username: '', category: '' });
              setIsUpsertOpen((v) => !v);
            }}
          >
            {isUpsertOpen ? 'Close Add/Edit' : 'Add/Edit'}
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '200px 200px 160px 1fr',
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username</div>
          <input
            value={filters.username}
            onChange={(e) => setFilters((p) => ({ ...p, username: e.target.value }))}
            style={inputStyle}
            placeholder="Select or type..."
            list="admin-watchlists-username-options"
          />
          <datalist id="admin-watchlists-username-options">
            {usernameOptions.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Category</div>
          <input
            value={filters.category}
            onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
            style={inputStyle}
            placeholder="Select or type..."
            list="admin-watchlists-category-options"
          />
          <datalist id="admin-watchlists-category-options">
            {categoryOptions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Symbol (client filter)</div>
          <input value={filters.symbol} onChange={(e) => setFilters((p) => ({ ...p, symbol: e.target.value }))} style={inputStyle} placeholder="e.g. AAPL" />
        </div>
        <div style={{ display: 'flex', alignItems: 'end', gap: 10 }}>
          <button type="button" style={buttonStyle('primary')} onClick={() => void refresh({ clearSelection: true })} disabled={loading}>
            Apply
          </button>
          <button
            type="button"
            style={buttonStyle('neutral')}
            onClick={() => {
              setFilters({ username: '', category: '', symbol: '' });
              setSelectedIds(new Set());
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {isUpsertOpen && (
        <div
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: colors.radius,
            padding: 12,
            marginBottom: 12,
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 900, color: colors.text, marginBottom: 10 }}>Upsert Watchlist Item</div>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 220px 220px 160px 160px 1fr', gap: 10, alignItems: 'end' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>User ID</div>
              <input
                value={draft.userId ? String(draft.userId) : ''}
                onChange={(e) => setDraft((p) => ({ ...p, userId: Number(e.target.value) || 0 }))}
                style={inputStyle}
                placeholder="required (or use Username/Category)"
              />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username</div>
              <input
                value={draft.username ?? ''}
                onChange={(e) => setDraft((p) => ({ ...p, username: e.target.value }))}
                style={inputStyle}
                placeholder="Select or type..."
                list="admin-watchlists-username-options"
              />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Category</div>
              <input
                value={draft.category ?? ''}
                onChange={(e) => setDraft((p) => ({ ...p, category: e.target.value }))}
                style={inputStyle}
                placeholder="Select or type..."
                list="admin-watchlists-category-options"
              />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Symbol</div>
              <input
                value={draft.symbol}
                onChange={(e) => setDraft((p) => ({ ...p, symbol: e.target.value.toUpperCase() }))}
                style={inputStyle}
                placeholder="AAPL"
              />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Buy Alert</div>
              <input
                value={draft.buyPriceAlert == null ? '' : String(draft.buyPriceAlert)}
                onChange={(e) => setDraft((p) => ({ ...p, buyPriceAlert: parseNumberOrNull(e.target.value) }))}
                style={inputStyle}
                placeholder="optional"
              />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Sell Alert</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  value={draft.sellPriceAlert == null ? '' : String(draft.sellPriceAlert)}
                  onChange={(e) => setDraft((p) => ({ ...p, sellPriceAlert: parseNumberOrNull(e.target.value) }))}
                  style={inputStyle}
                  placeholder="optional"
                />
                <button
                  type="button"
                  style={buttonStyle('primary', upserting)}
                  disabled={
                    upserting ||
                    !draft.symbol.trim() ||
                    (!draft.userId && !String(draft.username ?? '').trim() && !String(draft.category ?? '').trim())
                  }
                  onClick={async () => {
                    setUpserting(true);
                    setUpsertError(null);
                    try {
                      await adminApi.upsertWatchlistItem(token, {
                        ...draft,
                        symbol: normalizeSymbol(draft.symbol),
                      });
                      setIsUpsertOpen(false);
                      await refresh({ clearSelection: true });
                    } catch (e) {
                      if (isUnauthorized(e)) {
                        onUnauthorized();
                        return;
                      }
                      setUpsertError(e instanceof Error ? e.message : 'Failed saving watchlist item');
                    } finally {
                      setUpserting(false);
                    }
                  }}
                >
                  {upserting ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          {upsertError && <div style={{ marginTop: 10, color: colors.danger, fontSize: 13, fontWeight: 800 }}>{upsertError}</div>}
        </div>
      )}

      {error && <div style={{ marginBottom: 10, color: colors.danger, fontSize: 13, fontWeight: 800 }}>{error}</div>}

      <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: colors.radius }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={{ width: c.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={c.key === 'select' ? { ...headerCellStyle, textAlign: 'center' } : headerCellStyle}>
                  {c.key === 'select' ? (
                    <input
                      type="checkbox"
                      aria-label="Select all rows"
                      checked={filteredItems.length > 0 && filteredItems.every((x) => selectedIds.has(x.id))}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (checked) filteredItems.forEach((x) => next.add(x.id));
                          else filteredItems.forEach((x) => next.delete(x.id));
                          return next;
                        });
                      }}
                      style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                    />
                  ) : (
                    c.title
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(filteredItems ?? []).map((x, idx) => {
              const bg = idx % 2 === 0 ? 'transparent' : colors.hover;
              const isSelected = selectedIds.has(x.id);
              return (
                <tr
                  key={x.id}
                  style={{ backgroundColor: isSelected ? (isDarkMode ? colors.hover : '#EEF2FF') : bg, cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(x.id)) next.delete(x.id);
                      else next.add(x.id);
                      return next;
                    });
                  }}
                >
                  <td style={{ ...cellStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(x.id);
                          else next.delete(x.id);
                          return next;
                        });
                      }}
                      style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                    />
                  </td>
                  <td style={cellStyle}>{x.id}</td>
                  <td style={cellStyle}>{x.userId}</td>
                  <td style={cellStyle}>{x.username ?? ''}</td>
                  <td style={cellStyle}>{x.symbol}</td>
                  <td style={cellStyle}>{x.currentPrice ?? ''}</td>
                  <td style={cellStyle}>{x.changePct ?? ''}</td>
                  <td style={cellStyle}>{x.buyPriceAlert ?? ''}</td>
                  <td style={cellStyle}>{x.sellPriceAlert ?? ''}</td>
                  <td style={cellStyle}>{x.status}</td>
                  <td style={cellStyle}>{x.updatedAtUtc}</td>
                </tr>
              );
            })}

            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ ...cellStyle, color: colors.subText, textAlign: 'center', padding: 18 }}>
                  {loading ? 'Loading…' : 'No items'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminWatchlistsScreen;
